import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { AccountDataStore } from '../../account-data/account-data.store';
import { Account } from '../../account-data/account.model';
import { BrowserManagerDataDispatcher } from '../../browser-manager-data/browser-manager-data.dispatcher';
import { LocalStorageService } from '../../browser-manager-data/local-storage.service';
import {
  HOME_MASTER_ROUTE,
  ONBOARDING_MASTER_ROUTE,
} from '../../main/main.data';
import { NavigationService } from '../../navigation/navigation.service';
import { AccountMigration } from '../models/account-migration.model';
import {
  getOauthTokenKey,
  ONBOARDING_EXISTING_USER_ROUTE,
  ONBOARDING_NEW_USER_ROUTE,
  ONBOARDING_OAUTH_TOKEN_QUERY_PARAM,
  ONBOARDING_READY_ROUTE,
  ONBOARDING_WELCOME_INDUSTRY_ROUTE,
} from '../onboarding-data.data';
import { OnboardingDataDispatcher } from '../onboarding-data.dispatcher';
import { OnboardingDataStore } from '../onboarding-data.store';
import { map } from 'rxjs/operators';
import { FeaturesDataStore } from '../../features-data/features-data.store';
import { Toggles } from '../../features-data/sub-models/toggles.model';

@Injectable()
export class OnboardingUserGuard implements CanActivate {
  constructor(
    private _onboardingDataStore: OnboardingDataStore,
    private _onboardingDataDispatcher: OnboardingDataDispatcher,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _localStorageService: LocalStorageService,
    private _accountDataStore: AccountDataStore,
    private _navigationService: NavigationService,
    private _featureDataStore: FeaturesDataStore
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return forkJoin(
      this._onboardingDataStore.hasCompletedOnboarding(), // true for account migrated
      this._onboardingDataStore.getAccountMigration(),
      this._accountDataStore.getAccount(),
      this._featureDataStore.getFeatureToggles()
    ).pipe(
      map(
        ([hasCompletedOnboarding, accountMigration, account, toggles]: [
          boolean,
          AccountMigration,
          Account,
          Toggles
        ]) => {
          const urlPath: string = next.url[0].path;
          const isNewUserRouteActive = urlPath === ONBOARDING_NEW_USER_ROUTE;
          const isExistingUserRouteActive =
            urlPath === ONBOARDING_EXISTING_USER_ROUTE;
          const isWelcomeIndustryRouteActive = state.url.includes(
            ONBOARDING_WELCOME_INDUSTRY_ROUTE
          );
          const isReadyToGoRouteActive = state.url.includes(
            ONBOARDING_READY_ROUTE
          );
          const isNewUser = this._onboardingDataStore.isNewUser();

          if (!hasCompletedOnboarding) {
            if (isNewUser) {
              // check if user is eligible for FTU
              if (toggles.isAllAccess(Toggles.KEYS.FTU_EXPERIMENT)) {
                if (isWelcomeIndustryRouteActive) {
                  return true;
                }

                return isReadyToGoRouteActive || this.goToReadyToGoRoute();
              }

              return (
                isWelcomeIndustryRouteActive || this.goToWelcomeIndustryRoute()
              );
            } else {
              return accountMigration.isAccountMigrated
                ? isExistingUserRouteActive || this.goToExistingUserRoute()
                : this.goToHome();
            }
          } else if (isNewUserRouteActive) {
            // for errors on InvoiceOptionsComponent.saveCardPayments() on Stripe flow, allow new user onboarding route
            const tokenFromQueryParam = next.queryParamMap.get(
              ONBOARDING_OAUTH_TOKEN_QUERY_PARAM
            );
            const oauthTokenKey = getOauthTokenKey(account.accountId);
            const oauthTokenValue = this._localStorageService.getItem(
              oauthTokenKey
            );

            if (
              tokenFromQueryParam &&
              oauthTokenValue &&
              tokenFromQueryParam === oauthTokenValue
            ) {
              this._browserManagerDataDispatcher.removeLocalStorageItem(
                oauthTokenKey
              );

              return true;
            }
          }

          // otherwise go to home
          return this.goToHome();
        }
      )
    );
  }

  goToWelcomeIndustryRoute(): boolean {
    this._navigationService.goTo(
      [
        ONBOARDING_MASTER_ROUTE,
        ONBOARDING_NEW_USER_ROUTE,
        ONBOARDING_WELCOME_INDUSTRY_ROUTE,
      ],
      { replaceUrl: true }
    );

    return false;
  }

  goToReadyToGoRoute(): boolean {
    this._navigationService.goTo(
      [ONBOARDING_MASTER_ROUTE, ONBOARDING_READY_ROUTE],
      {
        replaceUrl: true,
      }
    );

    return false;
  }

  goToExistingUserRoute(): boolean {
    this._navigationService.goTo(
      [ONBOARDING_MASTER_ROUTE, ONBOARDING_EXISTING_USER_ROUTE],
      {
        replaceUrl: true,
      }
    );

    return false;
  }

  goToHome(): boolean {
    this._onboardingDataDispatcher.markOnboardingAsCompleted();

    // set timeout so onboarding can be marked as completed before redirect to home
    setTimeout(() => this._navigationService.goToByUrl(HOME_MASTER_ROUTE));

    return false;
  }
}
